import React, { useState, useEffect, useRef, useMemo } from "react";
import "./RequestForFirm.css";
import type {
  TBunkerPrice,
  TCurrentVessel,
  TPort,
  TWeather,
  TRequestForFirm,
} from "@/types";
import { createRequestForFirm } from "../constructors";
import VeselIcon from "./VesselIcon";
import ApiClient from "../services/fuelandmoreapi";
import { getForecastColorsFromWeatherHours } from "../utilities/weatherUtils";
import PortWeatherForecast from "./PortWeatherForecast";
import { config } from "../config";
import { formatUTC } from "../utils/date";
import { getWeatherForPortCached } from "../services/weatherService";

interface ClusteredBunkerPrice {
  bunkerMarkers: TPort[];
  prices: TBunkerPrice[];
  weather: Map<string, TWeather[]>;
}

interface RequestForFirmProps {
  clusteredBunkerPrice: ClusteredBunkerPrice;
  vessel: TCurrentVessel | null;
  fromPort: string;
  toPort: string;
  toEta: string;
  onCancel?: () => void;
  initialPort?: string; // optional port unlocode to auto-open page 2
}

const getRiskColor = (score: number | null) => {
  if (score === null || score === undefined) return "";
  if (score > 70) return "risk-score-red";
  if (score > 30) return "risk-score-amber";
  return "risk-score-green";
};

const RequestForFirm: React.FC<RequestForFirmProps> = ({
  clusteredBunkerPrice,
  vessel,
  fromPort,
  toPort,
  toEta,
  onCancel,
  initialPort,
}) => {
  // ...existing code...
  const [page, setPage] = useState<0 | 1>(0); // 0 select port, 1 line items
  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<
    {
      fuelGrade: string;
      indicativePrice: number;
      quantity: string; // as entered
      unitOfMeasure: string;
      timestamp: Date;
    }[]
  >([]);
  const [laycanFrom, setLaycanFrom] = useState("");
  const [laycanTo, setLaycanTo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [weatherMap, setWeatherMap] = useState<Record<string, TWeather[]>>({});
  const [weatherDialogPort, setWeatherDialogPort] = useState<TPort | null>(
    null
  );
  const apiClientRef = useRef<ApiClient | null>(null);
  const { apiBaseUrl } = config;

  const [portNotes, setPortNotes] = useState<{ [unlocode: string]: string[] }>(
    {}
  );
  const [expandedNotes, setExpandedNotes] = useState<{
    [key: string]: boolean;
  }>({});
  useEffect(() => {
    // Fetch port notes on mount
    (async () => {
      try {
        const apiClient = new ApiClient(apiBaseUrl);
        const notes = await apiClient.getPortNotes();
        const grouped: { [unlocode: string]: string[] } = {};
        for (const note of notes) {
          const u = note.unlocode?.toLowerCase();
          if (!u) continue;
          if (!grouped[u]) grouped[u] = [];
          grouped[u].push(note.note);
        }
        setPortNotes(grouped);
      } catch {
        setPortNotes({});
      }
    })();
  }, [apiBaseUrl]);

  useEffect(() => {
    apiClientRef.current = new ApiClient(apiBaseUrl);
  }, [apiBaseUrl]);

  const groupedPrices = useMemo(
    () =>
      clusteredBunkerPrice.prices.reduce(
        (acc, p) => {
          (acc[p.bunkerId] ||= []).push(p);
          return acc;
        },
        {} as Record<string, TBunkerPrice[]>
      ),
    [clusteredBunkerPrice.prices]
  );

  useEffect(() => {
    const toFetch = Object.keys(groupedPrices).filter(
      (p) => !weatherMap[p.toLowerCase()]
    );
    if (!toFetch.length) return;
    toFetch.forEach(async (port) => {
      const w = await getWeatherForPortCached(port);
      setWeatherMap((prev) => ({ ...prev, [port.toLowerCase()]: w }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedPrices]);

  useEffect(() => {
    if (page === 1 && selectedPort) {
      const prices = groupedPrices[selectedPort] || [];
      if (!prices.length) {
        // Ensure we clear if no prices
        setLineItems([]);
      } else {
        setLineItems(
          prices.map((p) => ({
            fuelGrade: p.fuelGrade,
            indicativePrice: p.price,
            quantity: "",
            unitOfMeasure: "MT",
            timestamp: new Date(p.timestamp),
          }))
        );
      }
    }
  }, [page, selectedPort, groupedPrices]);

  const selectPort = (portKey: string) => {
    setSelectedPort(portKey);
    const prices = groupedPrices[portKey] || [];
    setLineItems(
      prices.map((p) => ({
        fuelGrade: p.fuelGrade,
        indicativePrice: p.price,
        quantity: "",
        unitOfMeasure: "MT",
        timestamp: new Date(p.timestamp),
      }))
    );
    setPage(1);
  };

  // If initialPort provided, auto-select once on mount / change
  useEffect(() => {
    if (initialPort && !selectedPort && page === 0) {
      const key = Object.keys(groupedPrices).find(
        (k) => k.toLowerCase() === initialPort.toLowerCase()
      );
      if (key) selectPort(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPort, groupedPrices]);

  const GetPortName = (unlocode: string) =>
    clusteredBunkerPrice.bunkerMarkers.find(
      (p) => p.unlocode.toLowerCase() === unlocode.toLowerCase()
    )?.name || "";
  const GetPortNameWithCountry = (unlocode: string) =>
    `${GetPortName(unlocode)} (${unlocode.slice(0, 2).toUpperCase()})`;
  const getPortObject = (unlocode: string): TPort | null =>
    clusteredBunkerPrice.bunkerMarkers.find(
      (p) => p.unlocode.toLowerCase() === unlocode.toLowerCase()
    ) || null;
  const renderWeatherForecastSVG = (colors: string[]) => (
    <svg width="100" height="10">
      {colors.map((c, i) => (
        <rect key={i} x={i * 8} y={0} width={6} height={10} fill={c} />
      ))}
    </svg>
  );

  // Timestamp display per spec: today (UTC) => hh:mm:ss UTC, otherwise YY/MM
  const formatBunkerDisplay = (
    value: string | number | Date | null | undefined
  ): string => {
    if (value === null || value === undefined || value === "") return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const sameUTCDate =
      d.getUTCFullYear() === now.getUTCFullYear() &&
      d.getUTCMonth() === now.getUTCMonth() &&
      d.getUTCDate() === now.getUTCDate();
    if (sameUTCDate) {
      const full = formatUTC(d, "datetime"); // dd/MM/yyyy hh:mm:ss UTC
      const parts = full.split(" ");
      // Expect [date, time, UTC]
      return parts.length >= 3 ? `${parts[1]} ${parts[2]}` : full;
    }
    const yy = String(d.getUTCFullYear() % 100).padStart(2, "0");
    const MM = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${yy}/${MM}`;
  };

  const handleSubmit = async () => {
    if (!vessel || !selectedPort) return;
    const prepared = lineItems
      .map((li) => ({
        fuelGrade: li.fuelGrade,
        indicativePrice: li.indicativePrice,
        quantity: parseFloat(li.quantity || "0"),
        unitOfMeasure: li.unitOfMeasure,
        timestamp: li.timestamp,
      }))
      .filter((li) => li.quantity > 0);
    if (!prepared.length) {
      alert("Please enter at least one quantity.");
      return;
    }
    setLoading(true);
    try {
      const req: TRequestForFirm = createRequestForFirm({
        clientDomain: localStorage.getItem("clientDomain") || "",
        vesselImo: vessel.imo,
        vesselName: vessel.name,
        destinationPort: toPort.replace(/[^a-zA-Z]/g, ""),
        eta: toEta,
        laycanFrom,
        laycanTo,
        remarks,
        port: selectedPort.replace(/[^a-zA-Z]/g, ""),
        lineItems: prepared.map((li) => ({
          fuelGrade: li.fuelGrade,
          quantity: li.quantity,
          unitOfMeasure: li.unitOfMeasure,
          indicativePrice: li.indicativePrice,
          timestamp: li.timestamp.getTime(),
        })),
      });
      await apiClientRef.current!.addRequestForFirm(req);
      alert("Request for Firm submitted successfully.");
      onCancel && onCancel();
    } catch (e) {
      console.error(e);
      alert("Failed to submit Request for Firm.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="request-for-firm">
      <button
        className="request-for-firm-close"
        type="button"
        onClick={() => onCancel && onCancel()}
        aria-label="Close"
      >
        ×
      </button>
      <div className="request-for-firm-header">
        <h2>
          Request for Firm
          {page === 1 && selectedPort && (
            <span className="selected-port-heading">
              {" "}
              – {GetPortNameWithCountry(selectedPort)}
            </span>
          )}
        </h2>
        <span className="vessel-name-row">
          <VeselIcon
            size={24}
            color={
              vessel?.riskScore != null
                ? getRiskColor(vessel.riskScore) === "risk-score-red"
                  ? "#ff3333"
                  : getRiskColor(vessel.riskScore) === "risk-score-amber"
                    ? "#ff9900"
                    : getRiskColor(vessel.riskScore) === "risk-score-green"
                      ? "#2ecc40"
                      : "black"
                : "black"
            }
          />
          <span className="vessel-name">
            {vessel?.name || "Unnamed Vessel"}
          </span>
          <span className="vessel-imo">
            {vessel?.imo ? ` (${vessel.imo})` : ""}
          </span>
          <span className="vessel-dwt">
            {vessel?.dwt ? ` ${Math.round(vessel.dwt / 1000)}K` : "N/A"}
          </span>
        </span>
        <div className="request-for-firm-subheader">
          {fromPort} to {toPort} on {toEta}
        </div>
      </div>

      {page === 0 && (
        <div className="price-cards-container">
          <div className="request-for-firm-body">
            {Object.entries(groupedPrices).map(([port, prices]) => {
              const weatherHours = weatherMap[port.toLowerCase()] || [];
              const colors = weatherHours.length
                ? getForecastColorsFromWeatherHours(weatherHours, "swellHeight")
                : [];
              const windColors = weatherHours.length
                ? getForecastColorsFromWeatherHours(weatherHours, "windSpeed")
                : [];
              const notes = portNotes[port.toLowerCase()] || [];
              return (
                <div className="request-for-firm-card" key={port}>
                  <div className="request-for-firm-subheader port">
                    {GetPortNameWithCountry(port)}
                  </div>
                  {/* Weather Section */}
                  <div
                    className="weather-forecast"
                    role="button"
                    title="Click for 10-day weather forecast"
                    style={{ cursor: colors.length ? "pointer" : "default" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const po = getPortObject(port);
                      if (po) setWeatherDialogPort(po);
                    }}
                  >
                    <span className="weather-type">Swell:</span>
                    {colors.length ? (
                      renderWeatherForecastSVG(colors)
                    ) : (
                      <span style={{ marginLeft: 8 }}>
                        No forecast available
                      </span>
                    )}
                  </div>
                  <div
                    className="weather-forecast"
                    role="button"
                    title="Click for 10-day weather forecast"
                    style={{
                      cursor: windColors.length ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const po = getPortObject(port);
                      if (po) setWeatherDialogPort(po);
                    }}
                  >
                    <span className="weather-type">Wind speed:</span>
                    {windColors.length ? (
                      renderWeatherForecastSVG(windColors)
                    ) : (
                      <span style={{ marginLeft: 8 }}>
                        No forecast available
                      </span>
                    )}
                  </div>
                  {/* Table of prices */}
                  <table
                    className="request-for-firm-table"
                    style={{ cursor: "pointer" }}
                    title="Select this port"
                    onClick={() => selectPort(port)}
                  >
                    <thead>
                      <tr>
                        <th className="grade">Grade</th>
                        <th>USD</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prices.map((price, i) => (
                        <tr key={i}>
                          <td className={`grade ${price.fuelGrade}`}>
                            {price.fuelGrade}
                          </td>
                          <td>
                            {price.price.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td>{formatBunkerDisplay(price.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Port Notes Section (after table, above button) */}
                  {notes.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {notes.map((note, idx) => {
                        const words = note.split(" ");
                        const isLong = words.length > 12;
                        const key = `${port}-${idx}`;
                        const expanded = expandedNotes[key];
                        return (
                          <div
                            key={key}
                            style={{
                              color: "#b36b00",
                              background: "#fff8e1",
                              borderRadius: 6,
                              padding: "2px 8px",
                              margin: "2px 0",
                              fontSize: 11,
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <span
                              style={{ fontWeight: "bold", marginRight: 6 }}
                            >
                              ({idx + 1})
                            </span>
                            <span style={{ flex: 1 }}>
                              {!isLong || expanded ? (
                                <>
                                  {note}
                                  {isLong && (
                                    <span
                                      style={{
                                        color: "#0077cc",
                                        cursor: "pointer",
                                        marginLeft: 8,
                                      }}
                                      onClick={() =>
                                        setExpandedNotes((prev) => ({
                                          ...prev,
                                          [key]: false,
                                        }))
                                      }
                                    >
                                      less
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {words.slice(0, 12).join(" ")}...
                                  <span
                                    style={{
                                      color: "#0077cc",
                                      cursor: "pointer",
                                      marginLeft: 8,
                                    }}
                                    onClick={() =>
                                      setExpandedNotes((prev) => ({
                                        ...prev,
                                        [key]: true,
                                      }))
                                    }
                                  >
                                    more
                                  </span>
                                </>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ marginTop: 8, textAlign: "right" }}>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => selectPort(port)}
                      style={{ padding: "4px 8px" }}
                    >
                      Select Port
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {page === 1 && selectedPort && (
        <div className="request-for-firm-page-two">
          <div className="page-two-scroll">
            <div className="table-wrapper">
              <table
                className="request-for-firm-table"
                style={{ width: "100%" }}
              >
                <thead>
                  <tr>
                    <th className="grade">Grade</th>
                    <th>USD</th>
                    <th>Timestamp</th>
                    <th>Quantity</th>
                    <th>UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", fontStyle: "italic" }}
                      >
                        No prices available for this port.
                      </td>
                    </tr>
                  )}
                  {lineItems.map((li, idx) => (
                    <tr key={li.fuelGrade}>
                      <td className={`grade ${li.fuelGrade}`}>
                        {li.fuelGrade}
                      </td>
                      <td>
                        {li.indicativePrice.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>{formatBunkerDisplay(li.timestamp)}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={li.quantity}
                          onChange={(e) =>
                            setLineItems((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, quantity: e.target.value }
                                  : p
                              )
                            )
                          }
                          style={{ width: "90px" }}
                          disabled={loading || lineItems.length === 0}
                        />
                      </td>
                      <td>
                        <select
                          value={li.unitOfMeasure}
                          onChange={(e) =>
                            setLineItems((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, unitOfMeasure: e.target.value }
                                  : p
                              )
                            )
                          }
                          disabled={loading || lineItems.length === 0}
                        >
                          <option value="MT">MT</option>
                          <option value="cbm">cbm</option>
                          <option value="KL">KL</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="laycan-remarks-wrapper">
              <div className="form-row four">
                <div className="field-block">
                  <label>Laycan From:</label>
                  <input
                    type="date"
                    value={laycanFrom}
                    onChange={(e) => setLaycanFrom(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="field-block">
                  <label>Laycan To:</label>
                  <input
                    type="date"
                    value={laycanTo}
                    onChange={(e) => setLaycanTo(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="field-block" style={{ gridColumn: "span 2" }}>
                  <label>Remarks:</label>
                  <textarea
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="request-for-firm-footer">
            {initialPort ? (
              <button
                type="button"
                className="secondary-button"
                onClick={() => onCancel && onCancel()}
                disabled={loading}
                style={{ width: "100%" }}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setPage(0);
                  setSelectedPort(null);
                  setLineItems([]);
                }}
                disabled={loading}
                style={{ width: "100%" }}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="secondary-button"
              onClick={handleSubmit}
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      )}

      <PortWeatherForecast
        port={weatherDialogPort}
        weather={
          weatherDialogPort
            ? weatherMap[weatherDialogPort.unlocode.toLowerCase()]
            : undefined
        }
        open={!!weatherDialogPort}
        onClose={() => setWeatherDialogPort(null)}
      />
    </div>
  );
};

export default RequestForFirm;
